import * as tslib_1 from "tslib";
/**
 * @file Component that summarizes a request.
 * @author Maarten Lensink
 */
import { OnDestroy, EventEmitter } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { EntityQuery } from 'breeze-client';
import * as moment from 'moment';
import { DomainService } from '../../domain.service';
import { ResourceService } from '../../resource.service';
import { addZero } from '../../base/helpers/addZero';
import { AbstractBase } from '../../core/helpers/abstract-base';
import { LocalisationService } from '../../core/localisation.service';
import { Request } from '../request';
/**
 * Displays a summary of a request.
 */
var RequestSummaryComponent = /** @class */ (function (_super) {
    tslib_1.__extends(RequestSummaryComponent, _super);
    /**
     * Creates an instance of RequestSummaryComponent.
     * @param {DomainService} domainService The domain service.
     * @param {TranslateService} translateService The translation service.
     * @param {LocalisationService} localisationService The localisation service.
     * @memberOf RequestSummaryComponent
     */
    function RequestSummaryComponent(domainService, translateService, resourceService, localisationService) {
        var _this = _super.call(this) || this;
        _this.domainService = domainService;
        _this.translateService = translateService;
        _this.resourceService = resourceService;
        _this.localisationService = localisationService;
        /**
         * Subscription on request changes.
         * @private
         * @type {number}
         */
        _this.requestSubscription = undefined;
        /**
         * Gets, sets the receiver entity of the request.
         * @private
         * @type {Entity}
         */
        _this.receiver = undefined;
        /**
         * Gets, sets the subject entity of the request.
         * @private
         * @type {Entity}
         */
        _this.subject = undefined;
        /**
         * Gets, sets the target entity of the request.
         * @private
         * @type {Entity}
         */
        _this.target = undefined;
        /** The name of the given task. */
        _this.subjectDescription = '';
        /** The time the given task takes. */
        _this.subjectEffort = '';
        /** The time the given task spans. */
        _this.subjectTimespan = '';
        /** The plan items on the subject day. */
        _this.subjectItems = [];
        /** The name of the receiver. */
        _this.receiverName = '';
        /** The name of the received task. */
        _this.targetDescription = '';
        /** The time the received tasks takes. */
        _this.targetEffort = '';
        /** The time the received tasks spans. */
        _this.targetTimespan = '';
        /** The plan items on the target day. */
        _this.targetItems = [];
        /** Send targetItems to the parent */
        _this.targetItemsChange = new EventEmitter();
        return _this;
    }
    Object.defineProperty(RequestSummaryComponent.prototype, "request", {
        /**
         * Gets or sets the request that is summarized.
         * @type {Request}
         * @memberOf RequestSummaryComponent
         */
        get: function () {
            return this._request;
        },
        set: function (request) {
            var _this = this;
            if (this._request === request) {
                return;
            }
            if (this._request && this.requestSubscription) {
                this._request.entityAspect.propertyChanged.unsubscribe(this.requestSubscription);
            }
            this._request = request;
            this.subjectDescription = '';
            this.subjectEffort = '';
            this.subjectTimespan = '';
            this.receiverName = '';
            this.targetDescription = '';
            this.targetEffort = '';
            this.targetTimespan = '';
            if (this._request) {
                this.requestSubscription = this._request.entityAspect.propertyChanged.subscribe(function () {
                    _this.getEntities();
                });
            }
            this.getEntities();
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(RequestSummaryComponent.prototype, "targetConfiguration", {
        /**
         * Gets or sets the configuration for the relation component displaying the PlanItems on the target day.
         * @readonly
         * @type {ComponentConfiguration}
         * @memberOf RequestSummaryComponent
         */
        get: function () {
            return this._targetConfiguration;
        },
        set: function (value) {
            if (value !== this._targetConfiguration) {
                this._targetConfiguration = value;
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(RequestSummaryComponent.prototype, "requestType", {
        /**
         * Returns the request type, used in a switch case in the template.
         * @readonly
         * @type {string}
         * @memberOf RequestSummaryComponent
         */
        get: function () {
            if (!this.request) {
                return '';
            }
            switch (this.request.Type) {
                case 'REASSIGN-ASSIGNMENT':
                case 'REASSIGN-ATTENDANT':
                case 'REASSIGN-DAY':
                    return 'REASSIGN';
                case 'EXCHANGE-ASSIGNMENT':
                case 'EXCHANGE-ATTENDANT':
                case 'EXCHANGE-DAY':
                    return 'EXCHANGE';
                default:
                    return this.request.Type;
            }
        },
        enumerable: true,
        configurable: true
    });
    /**
     * Destroys the request property changed subscription.
     * @memberOf RequestSummaryComponent
     */
    RequestSummaryComponent.prototype.ngOnDestroy = function () {
        _super.prototype.ngOnDestroy.call(this);
        if (this._request && this.requestSubscription) {
            this._request.entityAspect.propertyChanged.unsubscribe(this.requestSubscription);
        }
    };
    /**
     * Returns a datetime on the given date at the end of the day.
     * @param {Date} date The date, which should be at start of day.
     * @returns {Date} The date at the end of the day.
     * @memberOf RequestSummaryComponent
     */
    RequestSummaryComponent.prototype.endOf = function (date) {
        if (!date) {
            return null;
        }
        var result = new Date(date.valueOf());
        result.setDate(date.getDate() + 1);
        return result;
    };
    /**
     * Retrieves the entities needed to write the summary.
     * @private
     * @memberOf RequestSummaryComponent
     */
    RequestSummaryComponent.prototype.getEntities = function () {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var entityType, _a, subjectQuery, response, targetQuery, response, subjectType, _b, _c;
            var _this = this;
            return tslib_1.__generator(this, function (_d) {
                switch (_d.label) {
                    case 0:
                        if (!this.request) {
                            this.receiver = null;
                            this.subject = null;
                            this.target = null;
                            this.subjectItems.length = 0;
                            this.targetItems.length = 0;
                            return [2 /*return*/];
                        }
                        if (!!this.request.ReceiverOid) return [3 /*break*/, 1];
                        this.receiver = null;
                        return [3 /*break*/, 3];
                    case 1:
                        if (!(!this.receiver || this.receiver['Oid'] !== this.request.ReceiverOid)) return [3 /*break*/, 3];
                        entityType = this.domainService.getEntityType('Resource');
                        _a = this;
                        return [4 /*yield*/, this.domainService.getEntityByKey(entityType, this.request.ReceiverOid, true).toPromise()];
                    case 2:
                        _a.receiver = (_d.sent());
                        _d.label = 3;
                    case 3:
                        if (!(this.request.SubjectType === 'Day')) return [3 /*break*/, 10];
                        this.subject = null;
                        this.target = null;
                        if (!!moment.isDate(this.request.SubjectDay)) return [3 /*break*/, 4];
                        this.subjectItems.length = 0;
                        return [3 /*break*/, 6];
                    case 4:
                        subjectQuery = new EntityQuery('TaskList');
                        subjectQuery.parameters = {
                            className: 'Resource',
                            oid: this.resourceService.getResourceOid(),
                            start: this.request.SubjectDay,
                            finish: moment(this.request.SubjectDay).clone().endOf('day').toDate(),
                            include: 'Attendants Assignments'
                        };
                        subjectQuery = subjectQuery.expand('Item');
                        return [4 /*yield*/, this.domainService.entityManager.executeQuery(subjectQuery)];
                    case 5:
                        response = _d.sent();
                        this.subjectItems.length = 0;
                        response.results.forEach(function (item) {
                            _this.subjectItems.push(item['Item']);
                        });
                        _d.label = 6;
                    case 6:
                        if (!!moment.isDate(this.request.TargetDay)) return [3 /*break*/, 7];
                        this.targetItems.length = 0;
                        return [3 /*break*/, 9];
                    case 7:
                        targetQuery = new EntityQuery('TaskList');
                        targetQuery.parameters = {
                            className: 'Resource',
                            oid: this.request.ReceiverOid,
                            start: this.request.TargetDay,
                            finish: moment(this.request.TargetDay).clone().endOf('day').toDate(),
                            include: 'Attendants Assignments'
                        };
                        targetQuery = targetQuery.expand('Item');
                        return [4 /*yield*/, this.domainService.entityManager.executeQuery(targetQuery)];
                    case 8:
                        response = _d.sent();
                        this.targetItems.length = 0;
                        response.results.forEach(function (item) {
                            _this.targetItems.push(item['Item']);
                        });
                        this.targetItemsChange.emit(this.targetItems);
                        _d.label = 9;
                    case 9: return [3 /*break*/, 16];
                    case 10:
                        this.subjectItems.length = 0;
                        this.targetItems.length = 0;
                        subjectType = void 0;
                        if (!!this.request.SubjectOid) return [3 /*break*/, 11];
                        this.subject = null;
                        return [3 /*break*/, 13];
                    case 11:
                        if (!(!this.subject || this.subject['Oid'] !== this.request.SubjectOid)) return [3 /*break*/, 13];
                        subjectType = this.domainService.getEntityType(this.request.SubjectType);
                        _b = this;
                        return [4 /*yield*/, this.domainService.getEntityByKey(subjectType, this.request.SubjectOid, true).toPromise()];
                    case 12:
                        _b.subject = (_d.sent());
                        _d.label = 13;
                    case 13:
                        if (!!this.request.TargetOid) return [3 /*break*/, 14];
                        this.target = null;
                        return [3 /*break*/, 16];
                    case 14:
                        if (!(!this.target || this.target['Oid'] !== this.request.TargetOid)) return [3 /*break*/, 16];
                        if (subjectType === undefined) {
                            subjectType = this.domainService.getEntityType(this.request.SubjectType);
                        }
                        _c = this;
                        return [4 /*yield*/, this.domainService.getEntityByKey(subjectType, this.request.TargetOid, true).toPromise()];
                    case 15:
                        _c.target = (_d.sent());
                        _d.label = 16;
                    case 16:
                        this.writeSummary();
                        return [2 /*return*/];
                }
            });
        });
    };
    /**
     * Creates the summary.
     * @memberOf RequestSummaryComponent
     */
    RequestSummaryComponent.prototype.writeSummary = function () {
        if (this.request && this.request.SubjectDay) {
            this.subjectDescription = this.getDayDescription(this.request.SubjectDay, this.subjectItems);
            this.subjectEffort = this.getEffortFromEntities(this.subjectItems);
            this.subjectTimespan = this.getTimespanFromEntities(this.subjectItems);
        }
        else if (this.subject) {
            this.subjectDescription = this.subject['Name'];
            this.subjectEffort = this.getEffortFromEntity(this.subject);
            this.subjectTimespan = this.getTimespanFromEntity(this.subject);
        }
        else {
            this.subjectDescription = '';
            this.subjectEffort = '';
            this.subjectTimespan = '';
        }
        this.receiverName = (this.receiver && this.receiver['Name']) ? (this.receiver['FirstName'] + ' ' + this.receiver['Prefix'] + ' ' + this.receiver['Name']) : '';
        if (this.request && this.request.TargetDay) {
            this.targetDescription = this.getDayDescription(this.request.TargetDay, this.targetItems);
            this.targetEffort = this.getEffortFromEntities(this.targetItems);
            this.targetTimespan = this.getTimespanFromEntities(this.targetItems);
        }
        else if (this.target) {
            this.targetDescription = this.target['Name'];
            this.targetEffort = this.getEffortFromEntity(this.target);
            this.targetTimespan = this.getTimespanFromEntity(this.target);
        }
        else {
            this.targetDescription = '';
            this.targetEffort = '';
            this.targetTimespan = '';
        }
    };
    /**
     * Returns a string representing the work load of a particular day.
     * @private
     * @param {moment.Moment} day The day to be re-assigned or exchanged.
     * @param {Entity[]} items The plan items to be re-assigned or exchanged.
     * @returns {string} A description of the workload on the given day.
     * @memberOf RequestSummaryComponent
     */
    RequestSummaryComponent.prototype.getDayDescription = function (day, items) {
        var result = '';
        if (items.length === 0) {
            result += this.translateService.instant('APP.NOTIFICATIONS.NOTHING_PLANNED');
        }
        else {
            var assignmentCount = items.filter(function (item) { return item.entityType.shortName === 'Assignment'; }).length;
            var attendantCount = items.filter(function (item) { return item.entityType.shortName === 'Attendant'; }).length;
            if (assignmentCount > 0) {
                result += assignmentCount + " " + this.translateService.instant('APP.MISC.ASSIGNMENT.' + (assignmentCount === 1 ? '#SINGULAR' : '#PLURAL')).toLowerCase();
            }
            if (assignmentCount && attendantCount) {
                result += " " + this.translateService.instant('APP.NOTIFICATIONS.AND') + " ";
            }
            if (attendantCount > 0) {
                result += attendantCount + " " + this.translateService.instant('APP.MISC.ATTENDANT.' + (attendantCount === 1 ? '#SINGULAR' : '#PLURAL')).toLowerCase();
            }
        }
        var date = moment(day);
        if (date.isValid()) {
            result += " " + this.translateService.instant('APP.NOTIFICATIONS.ON') + " " + date.format(this.localisationService.currentLanguage.formats.agenda.day.start);
        }
        return result;
    };
    /**
     * Returns a string representing the total effort of plan items.
     * @private
     * @param {Entity[]} items The sum of efforts is converted to a string.
     * @returns {string} The sum of efforts.
     * @memberOf RequestSummaryComponent
     */
    RequestSummaryComponent.prototype.getEffortFromEntities = function (items) {
        var effort = moment.duration();
        if (Array.isArray(items)) {
            items.forEach(function (item) {
                effort.add(moment.duration(item['Effort']).asHours() * item['SumFactor'], 'hours');
            });
        }
        if (effort.asMilliseconds() < 0) {
            return "-" + Math.floor(Math.abs(effort.asHours())) + ":" + addZero(Math.abs(effort.minutes()));
        }
        return Math.floor(effort.asHours()) + ":" + addZero(effort.minutes());
    };
    /**
     * Returns a string representing the effort of a plan item.
     * @private
     * @param {Entity} entity The entity's effort and timespan are represented.
     * @returns {string} The effort.
     * @memberOf RequestSummaryComponent
    * */
    RequestSummaryComponent.prototype.getEffortFromEntity = function (entity) {
        return this.getEffortFromEntities([entity]);
    };
    /**
     * Returns a string representing the total time span of plan items.
     * @private
     * @param {Entity[]} items The earliest start and latest finish of the items are searched.
     * @returns {string} The total time span.
     * @memberOf RequestSummaryComponent
     */
    RequestSummaryComponent.prototype.getTimespanFromEntities = function (items) {
        if (!items || items.length === 0) {
            return this.translateService.instant('APP.NOTIFICATIONS.NONE');
        }
        var start = moment(items[0]['Start']);
        var finish = moment(items[0]['Finish']);
        for (var index = 1; index < items.length; index++) {
            if (start.isAfter(items[index]['Start'])) {
                start = moment(items[index]['Start']);
            }
            if (finish.isBefore(items[index]['Finish'])) {
                finish = moment(items[index]['Finish']);
            }
        }
        var format = start.date() === finish.date() ? this.localisationService.time.format : this.localisationService.dateTime.format;
        var result = this.translateService.instant('APP.NOTIFICATIONS.FROM') + " " + start.format(format) + " ";
        result += this.translateService.instant('APP.NOTIFICATIONS.UNTIL') + " " + finish.format(format);
        return result;
    };
    /**
     * Returns a string representing the timespan of a plan item.
     * @private
     * @param {Entity} entity The entity's timespan are represented.
     * @returns {string} The timespan.
     * @memberOf RequestSummaryComponent
    * */
    RequestSummaryComponent.prototype.getTimespanFromEntity = function (entity) {
        var start = moment(entity['Start']);
        var finish = moment(entity['Finish']);
        var from = this.translateService.instant('APP.NOTIFICATIONS.FROM');
        var until = this.translateService.instant('APP.NOTIFICATIONS.UNTIL');
        var startDate = start.format(this.localisationService.date.format);
        var startTime = start.format(this.localisationService.time.format);
        var finishTime = finish.format(this.localisationService.time.format);
        if (start.date() === finish.date()) {
            return startDate + " " + from + " " + startTime + " " + until + " " + finishTime;
        }
        else {
            var finishDate = finish.format(this.localisationService.date.format);
            return from + " " + startDate + " " + startTime + " " + until + " " + finishDate + " " + finishTime;
        }
    };
    return RequestSummaryComponent;
}(AbstractBase));
export { RequestSummaryComponent };
